import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';
import pluralize from 'pluralize';
import { useOverlayContext, GridItem, useDevice, MobileSummaryCard, Row, Typography, Badge, Column, Grid, BottomSheetContentLayout } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { validateReviewers } from '~/business-logic/draftLogic';
import { useStore } from '~/contexts';
import { useContactsMap } from '~/hooks/reactQuery/derived/useContactsMap';
import { selectors } from '~/store/state-selectors';
import AddAdditionalRecipients from '../AddAdditionalRecipients/AddAdditionalRecipients';
import ContactDetails from '../ContactDetails/ContactDetails';
import SignatoryCheckbox from '../SignatoryCheckbox/SignatoryCheckbox';
import styles from './Recipients.module.scss';
export const { MobileRecipientsTID } = getTestIds();
function RecipientsBottomSheetContent(props) {
    const { contactDetailsProps, reviewers, showReviewerErrors, onReviewersChange, isSampleRelationship } = props;
    const { existingContact, disabled, onChange } = contactDetailsProps;
    const { popBottomSheetPage } = useOverlayContext();
    const worksAt = useStore(selectors.user.business);
    const { contactsMap, sampleContact } = useContactsMap(worksAt === null || worksAt === void 0 ? void 0 : worksAt.id);
    const [tempIsSampleRelationship, setTempIsSampleRelationship] = useState(isSampleRelationship);
    const [tempClientContactId, setTempClientContactId] = useState(existingContact === null || existingContact === void 0 ? void 0 : existingContact.id());
    const [tempReviewers, setTempReviewers] = useState(reviewers);
    const tempContact = tempIsSampleRelationship ? sampleContact : tempClientContactId ? contactsMap[tempClientContactId] : undefined;
    function handleSave() {
        const existingContactId = existingContact === null || existingContact === void 0 ? void 0 : existingContact.id();
        if (tempClientContactId !== existingContactId) {
            if (tempIsSampleRelationship) {
                onChange({
                    type: 'sampleContact'
                });
            } else if (!tempClientContactId) {
                onChange({
                    type: 'noContact'
                });
            } else {
                onChange({
                    type: 'existingContact',
                    id: tempClientContactId
                });
            }
        }
        if (!isEqual(reviewers, tempReviewers)) {
            onReviewersChange(tempReviewers);
        }
        popBottomSheetPage();
    }
    function handleChange(change) {
        switch(change.type){
            case 'existingContact':
                setTempIsSampleRelationship(false);
                setTempClientContactId(change.id);
                break;
            case 'sampleContact':
                setTempIsSampleRelationship(true);
                setTempClientContactId(sampleContact.id());
                break;
            case 'noContact':
                setTempIsSampleRelationship(false);
                setTempClientContactId(undefined);
                break;
        }
    }
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        header: {
            title: 'Recipients'
        },
        footer: {
            primaryAction: {
                text: 'Save',
                testId: MobileRecipientsTID.BottomSheet.Save(),
                onClick: handleSave
            }
        },
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.bottomSheetContent,
            children: /*#__PURE__*/ _jsxs(Grid, {
                align: "center",
                rowGap: 8,
                children: [
                    /*#__PURE__*/ _jsx(ContactDetails, _object_spread_props(_object_spread({}, contactDetailsProps), {
                        hideEditContact: true,
                        existingContact: tempContact,
                        onChange: handleChange
                    })),
                    /*#__PURE__*/ _jsx(AddAdditionalRecipients, {
                        inBottomSheet: true,
                        primaryContact: tempContact,
                        initialState: tempReviewers,
                        showReviewerErrors: showReviewerErrors,
                        disabled: disabled,
                        onChange: setTempReviewers
                    })
                ]
            })
        })
    });
}
function MobileRecipients(props) {
    const { contactDetailsProps, reviewers, showReviewerErrors } = props;
    const [showReviewerErrorsHappened, setShowReviewerErrorsHappened] = useState(showReviewerErrors);
    const { existingContact, showCustomerDetailsWarning, disabled } = contactDetailsProps;
    const { pushBottomSheetPage } = useOverlayContext();
    const hasReviewerErrors = useMemo(()=>!validateReviewers(reviewers, existingContact), [
        reviewers,
        existingContact
    ]);
    useEffect(()=>{
        if (showReviewerErrors) {
            setShowReviewerErrorsHappened(true);
        }
    }, [
        showReviewerErrors
    ]);
    function openRecipientsBottomSheet() {
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(RecipientsBottomSheetContent, _object_spread({}, props))
        });
    }
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 12,
        children: /*#__PURE__*/ _jsx(MobileSummaryCard, {
            title: "Recipients",
            editButtonTestId: MobileRecipientsTID.Card.Edit(),
            disabled: disabled,
            onEdit: openRecipientsBottomSheet,
            body: /*#__PURE__*/ _jsxs(Column, {
                gap: 4,
                children: [
                    /*#__PURE__*/ _jsx(Row, {
                        alignItems: "center",
                        gap: 4,
                        children: existingContact ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    testId: MobileRecipientsTID.Card.Primary(),
                                    children: existingContact.fullName()
                                }),
                                reviewers.length > 0 && /*#__PURE__*/ _jsx(Badge, {
                                    rounded: true,
                                    outlined: true,
                                    skin: "primary",
                                    testId: MobileRecipientsTID.Card.Badge(),
                                    children: "Primary contact"
                                })
                            ]
                        }) : /*#__PURE__*/ _jsx(Typography, {
                            testId: MobileRecipientsTID.Card.Primary(),
                            skin: showCustomerDetailsWarning ? 'error' : 'standard',
                            children: "Select a contact"
                        })
                    }),
                    reviewers.length > 0 && /*#__PURE__*/ _jsxs(Typography, {
                        testId: MobileRecipientsTID.Card.Reviewers(),
                        skin: hasReviewerErrors && showReviewerErrorsHappened ? 'error' : 'standard',
                        children: [
                            "+",
                            pluralize('additional recipient', reviewers.length, true)
                        ]
                    })
                ]
            })
        })
    });
}
export function Recipients(props) {
    const { contactDetailsProps, reviewers, showReviewerErrors, onReviewersChange, amendmentInfoCallout } = props;
    const { existingContact, disabled } = contactDetailsProps;
    const { isMobileDevice } = useDevice();
    return isMobileDevice ? /*#__PURE__*/ _jsx(MobileRecipients, _object_spread({}, props)) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ContactDetails, _object_spread({}, contactDetailsProps)),
            reviewers.length > 0 && /*#__PURE__*/ _jsx(GridItem, {
                span: 12,
                children: /*#__PURE__*/ _jsx(SignatoryCheckbox, {
                    info: 'The primary contact must sign the proposal.',
                    checkboxTestId: 'primary-contact-required-sign-checkbox',
                    value: true,
                    disabled: true,
                    infoTestId: 'primary-contact-required-sign-checkbox-info'
                })
            }),
            /*#__PURE__*/ _jsx(AddAdditionalRecipients, {
                primaryContact: existingContact,
                amendmentInfoCallout: amendmentInfoCallout,
                initialState: reviewers,
                showReviewerErrors: showReviewerErrors,
                disabled: disabled,
                onChange: onReviewersChange
            })
        ]
    });
}
